import React, { useState } from "react";
import "../../css/HomeCalculator.css";
import { motion } from "framer-motion";
import { FiClock, FiActivity, FiArrowRight, FiPercent } from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { Link } from "react-router-dom";

export const HomeCalculator = () => {
    const [loanAmount, setLoanAmount] = useState(25000);
    const [tenure, setTenure] = useState(30);
    const [interestRate, setInterestRate] = useState(1.0); // 0.25% to 1.0% daily
    const [processingFeeRate, setProcessingFeeRate] = useState(0.10); // 10% fee default

    // Dynamic Calculations
    const maxLoan = 100000;

    const interestRateVal = interestRate / 100; // actual daily rate
    const processingFee = loanAmount * processingFeeRate;
    const disbursedAmount = loanAmount - processingFee;
    const totalInterest = loanAmount * interestRateVal * tenure;
    const totalRepayable = loanAmount + totalInterest;
    
    // APR is dynamic and capped between 152.08% and 486.67%
    const effectiveTenureForApr = Math.min(tenure, 30);
    const calculatedApr = ((processingFee + totalInterest) / loanAmount) * (365 / effectiveTenureForApr) * 100;
    const apr = Math.max(152.08, Math.min(486.67, calculatedApr));

    // SVG Gauge Logic
    const circleRadius = 100;
    const circumference = 2 * Math.PI * circleRadius;
    // Fill percentage based on loan amount vs max loan
    const fillPercentage = loanAmount / maxLoan;
    const strokeDashoffset = circumference - (fillPercentage * circumference);

    return (
        <section className="hc_v7_wrapper">
            <div className="hc_v7_container">
                <div className="hc_v7_header">
                    <motion.span 
                        className="hc_v7_badge"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        SMART CALCULATOR
                    </motion.span>
                    <motion.h2 
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                    >
                        Predictive <span className="text_gradient_cyan">Financial Modeling.</span>
                    </motion.h2>
                </div>

                <motion.div 
                    className="hc_v7_dashboard"
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    {/* COLUMN 1: The Gauge */}
                    <div className="hc_v7_col hc_v7_gauge_col">
                        <div className="hc_v7_gauge_wrapper">
                            <svg className="hc_v7_svg" viewBox="0 0 240 240">
                                <defs>
                                    <linearGradient id="hc_grad_v7" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#2889a6" />
                                        <stop offset="100%" stopColor="#38bdf8" />
                                    </linearGradient>
                                    <filter id="hc_glow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor="#38bdf8" floodOpacity="0.4"/>
                                    </filter>
                                </defs>
                                
                                {/* Background Dashed Ring */}
                                <circle 
                                    cx="120" cy="120" r="108" 
                                    className="hc_v7_dashed_ring" 
                                />
                                
                                {/* Track Ring */}
                                <circle 
                                    cx="120" cy="120" r="90" 
                                    className="hc_v7_track" 
                                />
                                
                                {/* Animated Fill Ring */}
                                <motion.circle 
                                    cx="120" cy="120" r="90" 
                                    className="hc_v7_fill" 
                                    strokeDasharray={circumference}
                                    animate={{ strokeDashoffset }}
                                    transition={{ type: "spring", stiffness: 60, damping: 15 }}
                                    filter="url(#hc_glow)"
                                />
                            </svg>
                            
                            <div className="hc_v7_gauge_center">
                                <span className="hc_v7_label">TOTAL REPAYABLE</span>
                                <motion.h3 
                                    className="hc_v7_huge_number"
                                    key={totalRepayable}
                                    initial={{ scale: 0.8 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", stiffness: 200 }}
                                >
                                    ₹{Math.round(totalRepayable).toLocaleString()}
                                </motion.h3>
                                 <span className="hc_v7_sub">At {interestRate}% Daily Interest</span>
                            </div>
                        </div>
                    </div>

                    {/* COLUMN 2: Sliders */}
                    <div className="hc_v7_col hc_v7_sliders_col">
                        <div className="hc_v7_slider_box">
                            <div className="hc_v7_slider_top">
                                <span><FaRupeeSign className="icon_cyan" /> Loan Amount</span>
                                <strong>₹{loanAmount.toLocaleString()}</strong>
                            </div>
                            <input 
                                type="range" 
                                min="5000" max="100000" step="500"
                                value={loanAmount}
                                onChange={(e) => setLoanAmount(Number(e.target.value))}
                                className="hc_v7_range"
                                style={{ '--val': `${((loanAmount - 5000) / (100000 - 5000)) * 100}%` }}
                            />
                            <div className="hc_v7_slider_minmax">
                                <span>₹5,000</span>
                                <span>₹1,00,000</span>
                            </div>
                        </div>

                        <div className="hc_v7_slider_box">
                            <div className="hc_v7_slider_top">
                                <span><FiClock className="icon_blue" /> Repayment Tenure</span>
                                <strong>{tenure} Days</strong>
                            </div>
                            <input 
                                type="range" 
                                min="7" max="40" step="1"
                                value={tenure}
                                onChange={(e) => setTenure(Number(e.target.value))}
                                className="hc_v7_range range_blue"
                                style={{ '--val': `${((tenure - 7) / (40 - 7)) * 100}%` }}
                            />
                            <div className="hc_v7_slider_minmax">
                                <span>7 Days</span>
                                <span>40 Days</span>
                            </div>
                        </div>
                    </div>

                    {/* COLUMN 3: Stats & CTA */}
                    <div className="hc_v7_col hc_v7_stats_col">
                        <div className="hc_v7_stat_card">
                            <div className="hc_v7_stat_icon">
                                <FiActivity />
                            </div>
                            <div className="hc_v7_stat_text">
                                <span>APR</span>
                                <h3>{apr.toFixed(2)}%</h3>
                            </div>
                        </div>

                        <div className="hc_v7_mini_stats_grid">
                            <div className="hc_v7_mini_box">
                                <span>Processing Fee</span>
                                <select 
                                    value={processingFeeRate} 
                                    onChange={(e) => setProcessingFeeRate(Number(e.target.value))}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#0f172a',
                                        fontSize: '15px',
                                        fontWeight: '800',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        padding: '0',
                                        marginTop: '4px',
                                        fontFamily: 'inherit',
                                        width: '100%'
                                    }}
                                >
                                    <option value="0.10">10%</option>
                                    <option value="0.08">8%</option>
                                    <option value="0.06">6%</option>
                                    <option value="0.05">5%</option>
                                </select>
                            </div>
                            <div className="hc_v7_mini_box">
                                <span>Interest Rate</span>
                                <select 
                                    value={interestRate} 
                                    onChange={(e) => setInterestRate(Number(e.target.value))}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#0f172a',
                                        fontSize: '15px',
                                        fontWeight: '800',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        padding: '0',
                                        marginTop: '4px',
                                        fontFamily: 'inherit',
                                        width: '100%'
                                    }}
                                >
                                    <option value="1">1% Daily</option>
                                    <option value="0.75">0.75% Daily</option>
                                    <option value="0.5">0.5% Daily</option>
                                    <option value="0.25">0.25% Daily</option>
                                </select>
                            </div>
                        </div>

                        <Link to="/apply-now" className="hc_v7_cta" style={{ textDecoration: 'none' }}>
                            Apply Instantly <FiArrowRight className="cta_icon" />
                        </Link>
                    </div>

                </motion.div>
            </div>
        </section>
    );
};
